// Phase 1 — Backup Health Card (Super-Admin only).
// Shows last backup status, size, retention, and lets admin trigger a manual run.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, AlertTriangle, RefreshCw, Download, Database, Clock, Loader2,
} from "lucide-react";

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtAge(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `vor ${mins} Min.`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `vor ${hrs} Std.`;
    const days = Math.floor(hrs / 24);
    return `vor ${days} Tagen`;
  } catch {
    return iso;
  }
}

export default function BackupHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backup/status");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Backup-Status nicht laden");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function runNow() {
    setRunning(true);
    try {
      await api.post("/admin/backup/run");
      toast.success("Backup erfolgreich erstellt");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  }

  async function downloadBackup(filename) {
    try {
      const resp = await api.get(`/admin/backup/download/${filename}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    }
  }

  // Health computation
  const last = data?.last_run;
  const lastOk = last?.status === "ok";
  const lastIso = last?.finished_at || last?.started_at;
  const ageMs = lastIso ? Date.now() - new Date(lastIso).getTime() : null;
  const ageHours = ageMs == null ? null : ageMs / 3600000;
  const stale = ageHours != null && ageHours > 36;
  const banner = !last
    ? { tone: "warn", text: "Noch kein Backup gelaufen" }
    : !lastOk
      ? { tone: "error", text: `Letztes Backup fehlgeschlagen: ${last.error || "unbekannt"}` }
      : stale
        ? { tone: "warn", text: `Letztes erfolgreiches Backup ist >36h alt (${ageHours?.toFixed(1)}h)` }
        : { tone: "ok", text: "Backup-System gesund" };

  // Sparkline points: backup sizes over the last entries (newest right)
  const sparkPoints = (data?.backups || []).slice(0, 14).reverse();
  const maxSize = Math.max(1, ...sparkPoints.map((b) => b.size_bytes || 0));
  const minSize = Math.min(...sparkPoints.map((b) => b.size_bytes || 0));
  const sizeDelta = maxSize - minSize;
  const sizeAbnormal = sparkPoints.length >= 2 && sizeDelta > maxSize * 0.5;

  const toneCls = {
    ok: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    warn: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    error: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  }[banner.tone];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="backup-health-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-emerald-400" />
          <h3 className="font-display font-semibold text-zinc-100">Backup-Status</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading || running}
                  data-testid="backup-refresh"
                  className="p-1.5 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={runNow} disabled={running || loading}
                  data-testid="backup-run-now"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {running ? "Backup läuft..." : "Jetzt sichern"}
          </button>
        </div>
      </div>

      <div className={`border rounded p-3 mb-4 flex items-start gap-2 ${toneCls}`}
           data-testid="backup-banner">
        {banner.tone === "ok"
          ? <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
        <div className="text-xs">{banner.text}</div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
            <Stat label="Letztes Backup" value={fmtAge(lastIso)} testid="backup-stat-age" />
            <Stat label="Status" value={lastOk ? "OK" : "FEHLER"} accent={lastOk ? "emerald" : "rose"} testid="backup-stat-status"/>
            <Stat label="Anzahl gespeichert" value={data?.count ?? "—"} testid="backup-stat-count"/>
            <Stat label="Gesamt-Größe" value={fmtBytes(data?.total_bytes || 0)} testid="backup-stat-total"/>
          </div>

          {sparkPoints.length > 1 && (
            <div className="mb-4" data-testid="backup-sparkline">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                Größentrend (letzte {sparkPoints.length} Backups)
                {sizeAbnormal && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px]">
                    Auffällige Größenänderung
                  </span>
                )}
              </div>
              <Sparkline points={sparkPoints.map((b) => b.size_bytes || 0)} />
            </div>
          )}

          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Verlauf</div>
          <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="backup-history-list">
            {(data?.backups || []).slice(0, 20).map((b) => (
              <div key={b.filename}
                   className="flex items-center justify-between gap-3 px-2 py-1.5 hover:bg-zinc-800/50 rounded text-xs"
                   data-testid={`backup-row-${b.filename}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="w-3 h-3 text-zinc-500 shrink-0" />
                  <span className="font-mono truncate text-zinc-300">{b.filename}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-zinc-500 font-mono">{fmtBytes(b.size_bytes)}</span>
                  <button onClick={() => downloadBackup(b.filename)}
                          data-testid={`backup-download-${b.filename}`}
                          className="text-zinc-400 hover:text-emerald-400 p-1 rounded hover:bg-zinc-800">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {(!data?.backups || data.backups.length === 0) && (
              <div className="text-sm text-zinc-500 text-center py-3">Keine Backups vorhanden.</div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-zinc-800 text-[10px] text-zinc-600">
            Aufbewahrung: {data?.retention_days ?? 7} Tage · Verzeichnis: <code className="text-zinc-400">{data?.backup_dir}</code>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const accentCls = accent === "emerald" ? "text-emerald-400"
    : accent === "rose" ? "text-rose-400"
    : "text-zinc-100";
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-0.5 font-display font-semibold ${accentCls}`}>{value}</div>
    </div>
  );
}

function Sparkline({ points }) {
  const W = 240; const H = 36; const pad = 2;
  const max = Math.max(1, ...points);
  const min = Math.min(...points);
  const range = Math.max(1, max - min);
  const dx = (W - pad * 2) / Math.max(1, points.length - 1);
  const path = points
    .map((v, i) => {
      const x = pad + i * dx;
      const y = pad + (H - pad * 2) * (1 - (v - min) / range);
      return (i === 0 ? "M" : "L") + x.toFixed(1) + "," + y.toFixed(1);
    })
    .join(" ");
  return (
    <svg width={W} height={H} className="block">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-emerald-400" />
      {points.map((v, i) => (
        <circle key={i}
                cx={pad + i * dx}
                cy={pad + (H - pad * 2) * (1 - (v - min) / range)}
                r="1.4"
                className="fill-emerald-300" />
      ))}
    </svg>
  );
}
